import { Link } from "react-router-dom";
import { useSEO, buildBreadcrumbSchema } from "../hooks/useSEO";
import { AdSlot } from "./AdSlot";
import { ChevronRight } from "lucide-react";
import { getCalcCopy } from "../lib/calculatorCopy";

const DEFAULT_HOW_TO_STEPS = [
  {
    title: "Pick your filing status",
    body: "Single, Married Filing Jointly, Married Filing Separately, or Head of Household. Every bracket, threshold, and phase-out depends on this — get it right first.",
  },
  {
    title: "Enter your numbers",
    body: "Use gross (pre-tax) figures from your W-2, 1099s, or last year's return as a starting point. Annual figures, not monthly. Estimates are fine — the calculator is designed to work even when you only know rough numbers.",
  },
  {
    title: "Hit Calculate",
    body: "You'll see a side-by-side breakdown of 2025 versus 2026 numbers, with the difference called out. All calculations happen in your browser — nothing is sent to a server or stored.",
  },
  {
    title: "Read the results carefully",
    body: "Scroll the 'Understanding your results' section below — it explains the effective rate vs marginal rate, what's NOT included (state tax, FICA, etc.), and what to do next.",
  },
];

/**
 * Shared layout for every calculator page.
 * - Breadcrumb
 * - Title + intro
 * - "How to use this calculator" section
 * - Input form column (lg:col-span-5)
 * - Result column (lg:col-span-7)
 * - Education section below
 * - Related calculators CTA
 */
export const CalculatorPage = ({
  title,
  slug,
  overline,
  description,
  seoTitle,
  seoDescription,
  seoKeywords,
  inputPanel,
  resultPanel,
  howToSteps,
  education,
  relatedSlugs = [],
  jsonLd,
  children,
}) => {
  const url = `/calculator/${slug}`;
  const steps = howToSteps ?? getCalcCopy(slug).howToSteps ?? DEFAULT_HOW_TO_STEPS;
  useSEO({
    title: seoTitle || title,
    description: seoDescription || description,
    keywords: seoKeywords,
    path: url,
    jsonLd: jsonLd || buildBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Calculators", path: "/calculators" },
      { name: title, path: url },
    ]),
  });

  return (
    <>
      {/* Breadcrumb */}
      <div className="border-b border-foreground/20 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="font-mono text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2 flex-wrap" data-testid="breadcrumb">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/calculators" className="hover:text-primary">Calculators</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-foreground">{title}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="border-b-2 border-foreground py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-mono uppercase text-[10px] tracking-[0.3em] text-muted-foreground mb-3 block">
            {overline}
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-5 max-w-4xl" data-testid="calc-title">
            {title}
          </h1>
          <p className="font-body text-lg text-foreground/80 max-w-3xl leading-relaxed">
            {description}
          </p>
        </div>
      </section>

      {/* How to use this calculator */}
      {steps && steps.length > 0 && (
        <section className="border-b border-foreground/20 py-12 md:py-16 bg-card" data-testid="how-to-use">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-baseline justify-between mb-8 border-b-2 border-foreground pb-4">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold">
                How to use this calculator
              </h2>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground hidden sm:block">
                Takes about 30 seconds
              </span>
            </div>
            <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-foreground/20">
              {steps.map((step, i) => (
                <li
                  key={i}
                  className="p-6 border-r border-b border-foreground/20 bg-background"
                  data-testid={`howto-step-${i + 1}`}
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary mb-3 block">
                    Step {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-heading text-xl font-semibold mb-2 leading-tight">
                    {step.title}
                  </h3>
                  <p className="font-body text-sm text-foreground/80 leading-relaxed">
                    {step.body}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* Calculator */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-5">
              <div className="bg-card border-2 border-foreground brutalist-shadow p-6 md:p-8 lg:sticky lg:top-24" data-testid="calc-input-panel">
                <h2 className="font-heading text-2xl font-semibold mb-6 pb-4 border-b-2 border-foreground">
                  Your Inputs
                </h2>
                {inputPanel}
              </div>
            </div>
            <div className="lg:col-span-7" data-testid="calc-result-panel">
              {resultPanel}
            </div>
          </div>
        </div>
      </section>

      <AdSlot size="leaderboard" testid={`${slug}-mid-ad`} />

      {/* Educational content */}
      {education && (
        <section className="py-16 md:py-24 border-t-2 border-foreground bg-card">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="font-mono uppercase text-[10px] tracking-[0.3em] text-muted-foreground mb-3 block">
              The Context
            </span>
            {education}
          </div>
        </section>
      )}

      {/* Related calculators */}
      {relatedSlugs.length > 0 && (
        <section className="py-16 border-t border-foreground/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="font-heading text-2xl font-semibold mb-6">Related calculators</h3>
            <div className="flex flex-wrap gap-3">
              {relatedSlugs.map((r) => (
                <Link
                  key={r.slug}
                  to={`/calculator/${r.slug}`}
                  className="font-mono text-xs uppercase tracking-widest border border-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors"
                  data-testid={`related-${r.slug}`}
                >
                  {r.label} →
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {children}
    </>
  );
};
